import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Loader2, KeyRound } from 'lucide-react';
import { registerPasskey, AuthError } from '../../lib/auth';
import { useToast } from '../../components/ui/toast';
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Label,
  Logo,
} from '../../components/ui';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'form' | 'passkey'>('form');
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      addToast({ variant: 'destructive', title: 'Por favor, introduce tu email' });
      return;
    }

    setIsLoading(true);
    setStep('passkey');

    try {
      await registerPasskey(email);
      addToast({ variant: 'success', title: 'Passkey registrada correctamente' });
      navigate('/login');
    } catch (err) {
      setStep('form');
      if (err instanceof AuthError) {
        addToast({ variant: 'destructive', title: err.message });
      } else {
        addToast({ variant: 'destructive', title: 'Error durante el registro' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-2 text-center">
          <Logo size="xl" className="mb-2" />
          <p className="text-sm text-muted-foreground">Registro de Personal</p>
        </div>

        <Card className="shadow-lg">
          {step === 'form' ? (
            <>
              <CardHeader className="space-y-1">
                <CardTitle>Registrar Passkey</CardTitle>
                <CardDescription>
                  Configura tu acceso seguro sin contrasena
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@email.com"
                        autoComplete="email"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    loading={isLoading}
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Registrar Passkey
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    Tu cuenta debe existir en el sistema antes de registrar tu passkey.
                  </p>
                </form>
              </CardContent>
            </>
          ) : (
            <CardContent className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <KeyRound className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium">
                Sigue las instrucciones
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Tu navegador te pedira que uses tu huella, Face ID, o PIN
                para crear tu passkey.
              </p>
              <div className="mt-4">
                <div className="inline-flex items-center text-sm text-primary">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Esperando confirmacion...
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Ya tienes passkey?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Iniciar sesion
          </Link>
        </p>
      </div>
    </div>
  );
}
